import { constant, noop } from 'lodash';
import { useCallback, useMemo } from 'react';

import { type Board, type CardSize, type PublicBoard } from '@air/api/types';
import { type GallerySection } from '@air/component-gallery-view';
import { useBreakpointsContext } from '@air/provider-media-query';

import { type GalleryMetadata } from '~/components/Gallery/galleryMetadata';
import { GalleryItemType, type GalleryViewRender } from '~/components/Gallery/types';
import { useGallerySectionFooter } from '~/components/Gallery/useGallerySectionFooter';
import {
  type UseGallerySectionHeaderParams,
  useGallerySectionHeader,
} from '~/components/Gallery/useGallerySectionHeader';
import { isBoardDragType } from '~/components/Shared/Drag/dragTypes';
import { WKSPC_DESKTOP_HORIZONTAL_PADDING_WITH_SIDENAV_OPEN } from '~/constants/WorkspaceSpacing';
import useCSSGridLikeLayout from '~/hooks/useCSSGridLikeLayout';
import { type UseGalleryDataInfo } from '~/swr-hooks/gallery/types';

const DEFAULT_ITEM_HORIZONTAL_SPACING = 24;
const SMALL_SCREEN_ITEM_HORIZONTAL_SPACING = 8;
const SMALL_SCREEN_VERTICAL_ITEMS_SPACING = 8;
const MEDIUM_SCREEN_VERTICAL_ITEMS_SPACING = 24;

export const BoardGalleryCardDesktopSize: { [key in CardSize]: number } = {
  small: 153,
  medium: 204,
  large: 408,
  'extra-large': 612,
};

export const BoardGalleryCardMobileSize: { [key in CardSize]: number } = {
  small: 120,
  medium: 160,
  large: 220,
  'extra-large': 280,
};

export type UseGalleryBoardParams<B> = Pick<UseGallerySectionHeaderParams, 'renderHeader'> & {
  data?: UseGalleryDataInfo<B>;
  containerWidth: number;
  renderBoard: ((props: GalleryViewRender<B>) => JSX.Element) | undefined;
  loadMore?: () => void;
  containerHorizontalPadding?: number;
  headerContainerHeight?: number;
  itemHeight: number;
  itemWidth?: number;
  itemHorizontalSpacing?: number;
  isSelectable?: (board: B) => boolean;
  onSectionCollapse?: (isCollapsed: boolean) => void;
  rowVerticalSpacing?: number;
};

export const useGalleryBoards = <B extends Board | PublicBoard>({
  renderBoard,
  data,
  containerWidth,
  containerHorizontalPadding = WKSPC_DESKTOP_HORIZONTAL_PADDING_WITH_SIDENAV_OPEN,
  headerContainerHeight,
  loadMore = noop,
  itemHeight,
  itemWidth,
  itemHorizontalSpacing,
  isSelectable = constant(false),
  onSectionCollapse,
  renderHeader,
  rowVerticalSpacing,
}: UseGalleryBoardParams<B>): GallerySection<GalleryMetadata> | null => {
  const { isAboveSmallScreen, isAboveMediumScreen } = useBreakpointsContext();

  const { boxes: sectionBoxes } = useCSSGridLikeLayout({
    height: itemHeight,
    minWidth: itemWidth ?? itemHeight,
    itemCount: data?.items?.length ?? 0,
    containerWidth: containerWidth,
    itemHorizontalSpacing:
      itemHorizontalSpacing ??
      (!isAboveSmallScreen ? SMALL_SCREEN_ITEM_HORIZONTAL_SPACING : DEFAULT_ITEM_HORIZONTAL_SPACING),
    containerHorizontalPadding: containerHorizontalPadding,
  });

  const renderItem = useCallback(
    (index: number) => {
      const board = data?.items[index];

      return board && renderBoard
        ? renderBoard({
            box: sectionBoxes[index],
            data: board,
            index,
          })
        : null;
    },
    [data?.items, renderBoard, sectionBoxes],
  );

  const verticalItemsSpacing = useMemo(() => {
    let verticalSpacing = 36;

    if (!isAboveMediumScreen) {
      verticalSpacing = MEDIUM_SCREEN_VERTICAL_ITEMS_SPACING;
    }
    if (!isAboveSmallScreen) {
      verticalSpacing = SMALL_SCREEN_VERTICAL_ITEMS_SPACING;
    }

    return verticalSpacing;
  }, [isAboveMediumScreen, isAboveSmallScreen]);

  const getItemData = useCallback(
    (index: number): GalleryMetadata => ({
      type: GalleryItemType.board,
      isSelectable: data?.items[index] ? isSelectable(data?.items[index]) : false,
      itemId: data?.items[index]?.id || '',
      canDragTo: (item) => {
        const isAncestorOfAnyBoard = !!data?.items[index]?.ancestors?.find(({ id }) => id === item.id);

        if (isAncestorOfAnyBoard || item.mixedTypes) {
          return false;
        }

        return isBoardDragType(item.type);
      },
    }),
    [data?.items, isSelectable],
  );

  const { isHeaderCollapsed, header } = useGallerySectionHeader({
    isFirstSection: true,
    label: 'Boards',
    sectionType: GalleryItemType.board,
    itemCount: data?.total,
    containerHeight: headerContainerHeight,
    containerWidth,
    containerPadding: containerHorizontalPadding,
    onSectionCollapse,
    renderHeader,
  });

  const footer = useGallerySectionFooter({
    isLoading: !!data?.isLoading,
    loadMore,
    hasMore: !!data?.hasMore,
    containerWidth,
  });

  if (!data?.items?.length && !data?.isLoading) {
    return null;
  }

  return {
    header: data?.items?.length ? header : undefined,
    footer: isHeaderCollapsed ? undefined : footer,
    items: {
      render: renderItem,
      boxes: isHeaderCollapsed ? [] : sectionBoxes,
      verticalItemsSpacing: rowVerticalSpacing ?? verticalItemsSpacing,
      getData: getItemData,
    },
  };
};
